import React, { useState, useEffect } from "react";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { api, formatCurrency } from "../../utils/api";
import { Loading } from "../shared/EmptyState";

export const Informes = ({ adminToken }) => {
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState("mes");
  const [stats, setStats] = useState({
    ingresos: [],
    presupuestosPorEstado: [],
    facturasPorMes: [],
    topClientes: [],
    trabajosMasSolicitados: []
  });

  useEffect(() => {
    cargarDatos();
  }, [periodo]);

  const cargarDatos = async () => {
    try {
      const [presupuestos, facturas, clientes, trabajos] = await Promise.all([
        api.get("/presupuestos", adminToken),
        api.get("/facturas", adminToken),
        api.get("/clientes", adminToken),
        api.get("/trabajos")
      ]);

      // El selector de período define cuántos meses mostrar en la gráfica de
      // evolución, y qué presupuestos entran en "por estado"/"más solicitados"
      // (Top Clientes queda siempre a valor de vida completo — no tiene mucho
      // sentido un "top cliente de esta semana" en un negocio chico).
      const MESES_POR_PERIODO = { semana: 1, mes: 1, trimestre: 3, año: 12 };
      const DIAS_POR_PERIODO = { semana: 7, mes: 30, trimestre: 90, año: 365 };
      const mesesAMostrar = MESES_POR_PERIODO[periodo] || 6;
      const fechaDesde = new Date();
      fechaDesde.setDate(fechaDesde.getDate() - (DIAS_POR_PERIODO[periodo] || 30));
      const presupuestosPeriodo = presupuestos.filter(p => p.fecha && new Date(p.fecha) >= fechaDesde);

      // Ingresos — "Semana" mostraba exactamente lo mismo que "Mes" (ambos
      // mapeaban a 1 solo mes en el loop mensual de abajo), así que tocar
      // ese tab parecía no hacer nada. Para semana mostramos los últimos 7
      // días en vez de por mes — ahí sí es un gráfico distinto y con sentido.
      const ahora = new Date();
      const ingresosMensuales = [];
      if (periodo === "semana") {
        for (let i = 6; i >= 0; i--) {
          const dia = new Date(ahora);
          dia.setDate(dia.getDate() - i);
          const diaStr = dia.toLocaleDateString('es-ES', { weekday: 'short', day: '2-digit' });

          const facturasDelDia = facturas.filter(f => {
            const fechaFactura = new Date(f.fecha);
            return fechaFactura.toDateString() === dia.toDateString();
          });
          const presupuestosDelDia = presupuestos.filter(p => {
            const fechaPres = new Date(p.fecha);
            return fechaPres.toDateString() === dia.toDateString();
          });

          ingresosMensuales.push({
            mes: diaStr,
            facturado: facturasDelDia.reduce((sum, f) => sum + (f.total || 0), 0),
            presupuestado: presupuestosDelDia.reduce((sum, p) => sum + (p.total || 0), 0)
          });
        }
      } else {
      for (let i = mesesAMostrar - 1; i >= 0; i--) {
        const mes = new Date(ahora.getFullYear(), ahora.getMonth() - i, 1);
        const mesStr = mes.toLocaleDateString('es-ES', { month: 'short', year: 'numeric' });

        const facturasDelMes = facturas.filter(f => {
          const fechaFactura = new Date(f.fecha);
          return fechaFactura.getMonth() === mes.getMonth() &&
                 fechaFactura.getFullYear() === mes.getFullYear();
        });

        const presupuestosDelMes = presupuestos.filter(p => {
          const fechaPres = new Date(p.fecha);
          return fechaPres.getMonth() === mes.getMonth() &&
                 fechaPres.getFullYear() === mes.getFullYear();
        });

        ingresosMensuales.push({
          mes: mesStr,
          facturado: facturasDelMes.reduce((sum, f) => sum + (f.total || 0), 0),
          presupuestado: presupuestosDelMes.reduce((sum, p) => sum + (p.total || 0), 0)
        });
      }
      }

      // Presupuestos por estado (dentro del período elegido) — antes solo
      // contaba 4 de los 8 estados reales que usa el backend, así que
      // "en_ejecucion" (probablemente el más importante del día a día) no
      // aparecía en ningún lado de Informes.
      const estados = ['borrador', 'pendiente', 'en_revision', 'aceptado', 'en_ejecucion', 'finalizado', 'facturado', 'rechazado'];
      const presupuestosPorEstado = estados.map(estado => ({
        name: estado.charAt(0).toUpperCase() + estado.slice(1),
        value: presupuestosPeriodo.filter(p => p.estado === estado).length,
        total: presupuestosPeriodo.filter(p => p.estado === estado).reduce((sum, p) => sum + (p.total || 0), 0)
      }));

      // Top 5 clientes
      const clientesOrdenados = [...clientes]
        .sort((a, b) => (b.total_gastado || 0) - (a.total_gastado || 0))
        .slice(0, 5)
        .map(c => ({
          nombre: c.nombre,
          gastado: c.total_gastado || 0,
          presupuestos: c.total_presupuestos || 0
        }));

      // Trabajos más solicitados (dentro del período elegido)
      const trabajosCantidad = {};
      presupuestosPeriodo.forEach(p => {
        if (p.lineas) {
          p.lineas.forEach(linea => {
            if (!trabajosCantidad[linea.trabajo_nombre]) {
              trabajosCantidad[linea.trabajo_nombre] = 0;
            }
            trabajosCantidad[linea.trabajo_nombre]++;
          });
        }
      });

      const trabajosMasSolicitados = Object.entries(trabajosCantidad)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([nombre, cantidad]) => ({ nombre, cantidad }));

      setStats({
        ingresos: ingresosMensuales,
        presupuestosPorEstado,
        facturasPorMes: ingresosMensuales,
        topClientes: clientesOrdenados,
        trabajosMasSolicitados
      });
    } catch (e) {
      console.error("Error:", e);
    } finally {
      setLoading(false);
    }
  };

  const COLORS = {
    primary: "#1A3A4A",
    accent: "#D4AF37",
    success: "#10b981",
    warning: "#f59e0b",
    danger: "#ef4444",
    info: "#3b82f6"
  };

  // Mismo orden que el array "estados" de arriba (borrador, pendiente,
  // en_revision, aceptado, en_ejecucion, finalizado, facturado, rechazado).
  const PIE_COLORS = ["#9CA3AF", COLORS.warning, COLORS.info, COLORS.success, "#8b5cf6", "#059669", COLORS.accent, COLORS.danger];

  if (loading) return <Loading />;

  return (
    <div className="pb-24">
      <div className="container pt-4">
        <div className="mb-4">
          <h1 className="text-h2 font-display mb-1">📊 Informes y Reportes</h1>
          <p className="text-small text-secondary">Análisis del negocio</p>
        </div>

        {/* Selector de periodo */}
        <div className="tabs mb-6">
          <button onClick={() => setPeriodo("semana")} className={`tab ${periodo === "semana" ? "tab-active" : ""}`}>
            Semana
          </button>
          <button onClick={() => setPeriodo("mes")} className={`tab ${periodo === "mes" ? "tab-active" : ""}`}>
            Mes
          </button>
          <button onClick={() => setPeriodo("trimestre")} className={`tab ${periodo === "trimestre" ? "tab-active" : ""}`}>
            Trimestre
          </button>
          <button onClick={() => setPeriodo("año")} className={`tab ${periodo === "año" ? "tab-active" : ""}`}>
            Año
          </button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="card p-4 text-center">
            <div className="text-h3 font-display" style={{ color: COLORS.accent }}>
              {formatCurrency(stats.ingresos.reduce((sum, m) => sum + m.facturado, 0))}
            </div>
            <div className="text-tiny text-secondary mt-1">Total Facturado</div>
          </div>
          <div className="card p-4 text-center">
            <div className="text-h3 font-display" style={{ color: COLORS.primary }}>
              {stats.presupuestosPorEstado.reduce((sum, e) => sum + e.value, 0)}
            </div>
            <div className="text-tiny text-secondary mt-1">Presupuestos</div>
          </div>
          <div className="card p-4 text-center">
            <div className="text-h3 font-display" style={{ color: COLORS.success }}>
              {stats.presupuestosPorEstado.find(e => e.name === "Aceptado")?.value || 0}
            </div>
            <div className="text-tiny text-secondary mt-1">Aceptados</div>
          </div>
          <div className="card p-4 text-center">
            <div className="text-h3 font-display" style={{ color: COLORS.info }}>
              {stats.topClientes.length}
            </div>
            <div className="text-tiny text-secondary mt-1">Top Clientes</div>
          </div>
        </div>

        {/* Gráfica de Ingresos */}
        <div className="card p-4 mb-4">
          <h3 className="text-h3 font-display mb-4">💰 Evolución de Ingresos</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={stats.ingresos}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="mes" style={{ fontSize: "0.75rem" }} />
              <YAxis style={{ fontSize: "0.75rem" }} />
              <Tooltip 
                formatter={(value) => formatCurrency(value)}
                contentStyle={{ borderRadius: "8px", border: "1px solid #e5e7eb" }}
              />
              <Legend />
              <Line type="monotone" dataKey="facturado" stroke={COLORS.success} strokeWidth={3} name="Facturado" />
              <Line type="monotone" dataKey="presupuestado" stroke={COLORS.warning} strokeWidth={3} name="Presupuestado" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Gráficas en grid */}
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          {/* Presupuestos por estado */}
          <div className="card p-4">
            <h3 className="text-h3 font-display mb-4">📋 Presupuestos por Estado</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={stats.presupuestosPorEstado.filter((e) => e.value > 0)}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {stats.presupuestosPorEstado.filter((e) => e.value > 0).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={PIE_COLORS[stats.presupuestosPorEstado.indexOf(entry) % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Trabajos más solicitados */}
          <div className="card p-4">
            <h3 className="text-h3 font-display mb-4">🔧 Trabajos Más Solicitados</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={stats.trabajosMasSolicitados}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="nombre" style={{ fontSize: "0.7rem" }} angle={-45} textAnchor="end" height={80} />
                <YAxis style={{ fontSize: "0.75rem" }} />
                <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e5e7eb" }} />
                <Bar dataKey="cantidad" fill={COLORS.accent} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Clientes */}
        <div className="card p-4">
          <h3 className="text-h3 font-display mb-4">🏆 Top 5 Clientes</h3>
          <div className="space-y-3">
            {stats.topClientes.map((cliente, index) => (
              <div key={index} className="flex items-center justify-between p-3 rounded-lg" style={{ background: "var(--surface-secondary)" }}>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center font-bold"
                    style={{ background: COLORS.accent, color: "white", fontSize: "0.9rem" }}
                  >
                    {index + 1}
                  </div>
                  <div>
                    <div className="font-bold text-small">{cliente.nombre}</div>
                    <div className="text-tiny text-secondary">{cliente.presupuestos} presupuestos</div>
                  </div>
                </div>
                <div className="font-bold" style={{ color: COLORS.success }}>
                  {formatCurrency(cliente.gastado)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
